import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { SITE_URL } from "@/lib/utils";
import { useState, type FormEvent, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Reveal } from "@/components/reveal";
import { Eye, EyeOff } from "lucide-react";

export const Route = createFileRoute("/reset-password")({
  head: () => ({
    meta: [
      { title: "New password — Pluggz" },
      { name: "description", content: "Set a new password for your Pluggz account to secure your creator storefront, commissions, and saved shopping picks." },
      { name: "robots", content: "noindex" },
    ],
    links: [{ rel: "canonical", href: `${SITE_URL}/reset-password` }],
  }),
  component: ResetPasswordPage,
});

function ResetPasswordPage() {
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [valid, setValid] = useState(false);

  useEffect(() => {
    supabase.auth.onAuthStateChange((event) => {
      if (event === "PASSWORD_RECOVERY") setValid(true);
    });

    const hash = window.location.hash;
    if (hash.includes("type=recovery")) setValid(true);
  }, []);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setMessage(null);
    setLoading(true);
    const { error } = await supabase.auth.updateUser({ password });
    setLoading(false);
    if (error) return setError(error.message);
    setMessage("Password updated. Signing you in…");
    setTimeout(() => navigate({ to: "/" }), 1500);
  }

  return (
    <div className="relative mx-auto w-full max-w-md overflow-hidden px-4 py-12 sm:px-6 sm:py-20">
      <div aria-hidden className="pointer-events-none absolute -left-20 top-10 h-48 w-48 rounded-full bg-accent/20 blur-3xl float-slow sm:h-64 sm:w-64" />
      <div aria-hidden className="pointer-events-none absolute -right-20 bottom-20 h-48 w-48 rounded-full bg-sand/30 blur-3xl float-slow sm:h-64 sm:w-64" style={{ animationDelay: "-3s" }} />
      <div className="relative">
        <p className="editorial-eyebrow">Account recovery</p>
        <h1 className="mt-2 font-serif text-3xl sm:text-4xl">
          New <span className="italic shimmer">password</span>
        </h1>

        <form onSubmit={submit} className="mt-8 space-y-3">
          <div>
            <label htmlFor="reset-password" className="mb-1 block text-xs uppercase tracking-widest text-muted-foreground">New password</label>
            <div className="relative">
              <input
                id="reset-password"
                required
                type={showPassword ? "text" : "password"}
                autoComplete="new-password"
                minLength={6}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="At least 6 characters"
                className="w-full border border-border bg-card/80 backdrop-blur px-4 py-3 pr-10 text-base outline-none transition-colors focus:border-foreground sm:text-sm"
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>
          {error && <p className="text-xs text-destructive">{error}</p>}
          {message && <p className="text-xs text-accent">{message}</p>}
          <button
            disabled={loading || !valid}
            type="submit"
            className="w-full bg-foreground px-6 py-3 text-sm uppercase tracking-widest text-background transition-all duration-300 hover:scale-[1.01] disabled:opacity-50"
          >
            {loading ? "Updating…" : "Update password"}
          </button>
        </form>

        <p className="mt-8 text-center text-sm text-muted-foreground">
          <Link to="/login" className="link-underline text-foreground">Back to sign in</Link>
        </p>
      </div>
    </div>
  );
}
